import useLocales from '../locales/useLocales';
import { Language, ProductTranslation } from '../@types/product/product.type';

/**
 * Returns the correct translation for a product.
 * Priority: preferredLanguage (org default) > UI language > EN > first available
 *
 * @param preferredLanguage - Optional override (org's defaultLanguage). If not provided, falls back to UI language.
 */
export function useProductTranslation() {
  const { currentLang } = useLocales();

  // Map i18n lang value ('en', 'fr') to our backend Language enum ('EN', 'FR')
  const uiLanguageCode = currentLang.value.toUpperCase() as Language;

  const getTranslation = (
    translations: ProductTranslation[] | undefined,
    preferredLanguage?: Language
  ): ProductTranslation | undefined => {
    if (!translations || translations.length === 0) return undefined;
    const lang = preferredLanguage || uiLanguageCode;
    return (
      translations.find((t) => t.language === lang) ||
      translations.find((t) => t.language === 'EN') ||
      translations[0]
    );
  };

  const getName = (
    translations: ProductTranslation[] | undefined,
    preferredLanguage?: Language
  ): string => getTranslation(translations, preferredLanguage)?.name || '-';

  const getDescription = (
    translations: ProductTranslation[] | undefined,
    preferredLanguage?: Language
  ): string => getTranslation(translations, preferredLanguage)?.description || '-';

  return { getTranslation, getName, getDescription, uiLanguageCode };
}
